import { STORY_STATUSES, type ProjectDomainsResponse } from '@pulse/contracts';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { AttentionItem } from '@/components/pulse/attention-item';
import { AsOfPill } from '@/components/pulse/day';
import { AlertIcon, ChevronIcon, ExternalIcon } from '@/components/pulse/icons';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useProjectDomains } from '@/domains/projects/api';
import { ProjectTabs } from '@/domains/projects/project-tab';
import { useShell } from '@/domains/shell/api';

/** Each status's label and colour, as the prototype's STATUS paints them. */
const STATUS: Readonly<Record<(typeof STORY_STATUSES)[number], { label: string; colour: string }>> = {
  draft: { label: 'Draft', colour: 'var(--s-draft)' },
  'ready-for-dev': { label: 'Ready for dev', colour: 'var(--s-ready)' },
  'in-progress': { label: 'In progress', colour: 'var(--s-prog)' },
  'in-review': { label: 'In review', colour: 'var(--s-review)' },
  accepted: { label: 'Accepted', colour: 'var(--s-acc)' },
  blocked: { label: 'Blocked', colour: 'var(--s-block)' },
};

export type DomainSort = 'size' | 'done' | 'review' | 'accepted';
type DomainRow = ProjectDomainsResponse['domains'][number];

/**
 * PRJ-5's table as shown: with "Open stories only", accepted stories leave the counts and domains
 * whose every story is accepted are dropped (and named); then sorted by most stories (or most open),
 * the largest done share, most in review, or the largest accepted share. Done is built: in review
 * (waiting for acceptance) or accepted.
 */
export function shownDomains(domains: ReadonlyArray<DomainRow>, sort: DomainSort, openOnly: boolean): { rows: Array<DomainRow & { count: number; accepted: number; done: number }>; dropped: string[] } {
  const withCounts = domains.map((row) => ({ ...row, accepted: row.byStatus['accepted'] ?? 0, done: (row.byStatus['accepted'] ?? 0) + (row.byStatus['in-review'] ?? 0), count: openOnly ? row.total - (row.byStatus['accepted'] ?? 0) : row.total }));
  const dropped = openOnly ? withCounts.filter((row) => row.count === 0).map((row) => row.domain) : [];
  const rows = withCounts
    .filter((row) => !dropped.includes(row.domain))
    .map((row) => (openOnly ? { ...row, byStatus: Object.fromEntries(Object.entries(row.byStatus).filter(([status]) => status !== 'accepted')) } : row))
    .sort((a, b) => {
      if (sort === 'accepted') return b.accepted / b.total - a.accepted / a.total;
      if (sort === 'done') return b.done / b.total - a.done / a.total;
      if (sort === 'review') return (b.byStatus['in-review'] ?? 0) - (a.byStatus['in-review'] ?? 0);
      return b.count - a.count;
    });
  return { rows, dropped };
}

/** Projects › Domains (PRJ-5), built from the prototype's `viewDomains()`. An as-of page. */
export function ProjectDomainsPage(): React.ReactElement {
  const { productSlug = '' } = useParams();
  const page = useProjectDomains(productSlug);
  const shell = useShell();
  const data = page.data;
  return (
    <>
      <PageHeader
        eyebrow="Project · by domain"
        title={data?.product?.name ?? 'Projects'}
        sub={data?.product === null || data === undefined ? '' : `Every story in docs/stories on ${data.product.repositoryName}, grouped by the domain in its frontmatter.`}
        right={<AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />}
      />
      {page.isError ? <span className="sub">There is no project by that name.</span> : null}
      {data !== undefined ? <ProjectDomainsView data={data} productSlug={productSlug} /> : null}
    </>
  );
}

export function ProjectDomainsView({ data, productSlug, initialExpanded = null }: { readonly data: ProjectDomainsResponse; readonly productSlug: string; readonly initialExpanded?: string | null }): React.ReactElement {
  const [sort, setSort] = React.useState<DomainSort>('size');
  const [openOnly, setOpenOnly] = React.useState(false);
  const [expanded, setExpanded] = React.useState<string | null>(initialExpanded);
  const { rows, dropped } = shownDomains(data.domains, sort, openOnly);
  const sortButton = (key: DomainSort, label: string) => (
    <button type="button" aria-pressed={sort === key} onClick={() => setSort(key)}>
      {label}
    </button>
  );
  return (
    <>
      <ProjectTabs slug={productSlug} hasBoard hasStories view="domains" />
      {data.product === null ? (
        <span className="sub">This project has no story repository, so it has no domains.</span>
      ) : (
        <>
          <Kpis items={data.tiles.map((tile) => ({ label: tile.label, value: tile.value, sub: tile.sub, warn: tile.warn }))} />
          <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 320px' }}>
            <section className="card pad stack" style={{ gap: 12 }} aria-labelledby="dh">
              <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <h2 id="dh">Status by domain</h2>
                <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
                  <label className="check" htmlFor="open-only">
                    <input type="checkbox" id="open-only" checked={openOnly} onChange={(event) => setOpenOnly(event.target.checked)} />
                    Open stories only
                  </label>
                  <div className="seg" role="group" aria-label="Sort domains">
                    {sortButton('size', openOnly ? 'Most open' : 'Most stories')}
                    {sortButton('done', 'Most done')}
                    {sortButton('review', 'Most in review')}
                    {sortButton('accepted', 'Most accepted')}
                  </div>
                </div>
              </div>
              <div className="legend">
                {STORY_STATUSES.map((status) => (
                  <span key={status}>
                    <i className="sw" style={{ background: STATUS[status].colour }} />
                    {STATUS[status].label}
                  </span>
                ))}
              </div>
              <div className="dtable">
                <div className="drow headrow">
                  <span>Domain</span>
                  <span className="r">{openOnly ? 'Open' : 'Stories'}</span>
                  <span>Where each story is</span>
                  <span className="r" title="In review or accepted: built, whether or not accepted yet">
                    Done
                  </span>
                  <span className="r">Accepted</span>
                  <span className="r">AC passing</span>
                  <span />
                </div>
                {!data.recorded ? <span style={{ color: 'var(--muted)', padding: '10px 0' }}>Not collected yet</span> : null}
                {rows.map((row) => {
                  const present = STORY_STATUSES.filter((status) => (row.byStatus[status] ?? 0) > 0);
                  const summary = present.map((status) => `${row.byStatus[status]} ${STATUS[status].label.toLowerCase()}`).join(', ');
                  const open = expanded === row.domain;
                  return (
                    <React.Fragment key={row.domain}>
                      <button type="button" className="dbtn" aria-expanded={open} onClick={() => setExpanded(open ? null : row.domain)}>
                        <span style={{ fontWeight: 500 }}>
                          {row.domain}{' '}
                          {row.code !== null ? (
                            <span className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>
                              {row.code}
                            </span>
                          ) : null}
                        </span>
                        <span className="r num">{row.count}</span>
                        <span className="bar" role="img" aria-label={`${row.domain}: ${summary}`}>
                          {present.map((status) => (
                            <i
                              key={status}
                              style={{ width: `${(((row.byStatus[status] ?? 0) / row.count) * 100).toFixed(2)}%`, background: STATUS[status].colour }}
                              title={`${row.byStatus[status]} ${STATUS[status].label.toLowerCase()}`}
                            />
                          ))}
                        </span>
                        <span className="r num" style={{ fontWeight: 600 }}>
                          {Math.round((row.done / row.total) * 100)}%
                        </span>
                        <span className="r num" style={{ color: 'var(--ink-2)' }}>
                          {Math.round((row.accepted / row.total) * 100)}%
                        </span>
                        <span className="r num mono" style={{ fontSize: 13, color: 'var(--muted)' }}>
                          {row.acPassing} / {row.acTotal}
                        </span>
                        <span style={{ color: 'var(--muted)', transform: `rotate(${open ? 90 : 0}deg)`, display: 'inline-flex', justifyContent: 'center' }}>
                          <ChevronIcon />
                        </span>
                      </button>
                      {open ? (
                        <div className="ddetail">
                          {present.map((status) => (
                            <span key={status} className="chip c-kind">
                              <span className="sw" style={{ background: STATUS[status].colour }} />
                              {row.byStatus[status]} {STATUS[status].label.toLowerCase()}
                            </span>
                          ))}
                          <SourceLink className="linkish" href={row.folderUrl} style={{ fontSize: 13 }}>
                            Open the {row.domain} stories <ExternalIcon />
                          </SourceLink>
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
              {openOnly ? (
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  Accepted stories are hidden. {rows.reduce((count, row) => count + row.count, 0)} stories are still open.
                  {dropped.length > 0 ? ` Every story is accepted in ${dropped.join(', ')}, so ${dropped.length === 1 ? 'it is' : 'they are'} left out.` : ''}
                </span>
              ) : null}
            </section>
            <aside className="stack" style={{ gap: 16 }}>
              <section className="card pad stack" style={{ gap: 12 }}>
                <div className="row" style={{ gap: 8, color: 'var(--bad-ink)' }}>
                  <AlertIcon />
                  <h2 style={{ color: 'var(--ink)' }}>Blocked</h2>
                </div>
                {data.blocked.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>No story is blocked.</span> : null}
                {data.blocked.map((row) => (
                  <AttentionItem key={row.ref} row={row} />
                ))}
              </section>
              <section className="card pad stack" style={{ gap: 8 }}>
                <h2>Reading it</h2>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>AC passing counts acceptance criteria whose tests pass, from each story&apos;s frontmatter. Click a domain for its counts.</span>
              </section>
            </aside>
          </div>
        </>
      )}
    </>
  );
}
